import { memo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';

import { PageHeaderMeta } from '@/components/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { useAppSelector } from '@/redux/hooks';
import { getBaseUrl } from '@/redux/modules/config';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getFirstSearchItemId } from '@/redux/modules/search/search.selectors';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getItemCardImageUrl, isSimilarItemsPage, logoUrl } from '@/utils/urls';

type Props = {
    page: number;
    searchTerm: string;
};

const SearchPageHeaderMeta = ({ page, searchTerm }: Props) => {
    const { pathname } = useLocation();
    const similarItemsPage = isSimilarItemsPage(pathname);

    const itemId = useAppSelector(getFirstSearchItemId);
    const baseUrl = useAppSelector(getBaseUrl);

    const formatMessage = useAppFormatMessage();
    let title = String(
        formatMessage([similarItemsPage ? 'moreLikeResults' : 'searchTerms'], {
            values: { searchTerm: cleanSearchTerms(searchTerm) },
        })
    );

    if (page > 1) {
        title += String(formatMessage(['page_number_and_title'], { values: { page } }));
    }

    title = useAddNotificationCountToTitle(title);

    const description = String(
        formatMessage(['searchTermsDescription'], { values: { terms: cleanSearchTerms(searchTerm, true) } })
    );
    const canonical = `${baseUrl}/search/`;

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const firstPhoto = photos?.[0] || 0;
    const imageUrl = getItemCardImageUrl(sellerId, catalogId, itemId, firstPhoto, imageVersion) || logoUrl;

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={title}
        >
            <meta
                content="noindex, nofollow"
                name="robots"
            />
        </PageHeaderMeta>
    );
};

export default memo<Props>(SearchPageHeaderMeta, isEqual);
